import React from 'react';
import { IndianRupee, RefreshCw } from 'lucide-react';

import { formatIST } from '@/lib/utils';
import {
  EmptyState, IconButton, PageHeader, PhonePage, ScreenLoader, SearchBar, StatusPill, TabRow,
} from '@/components/phone/primitives';
import { Colors } from '@/components/phone/theme';

/**
 * What is owed and what just came in, on the accountant's phone.
 *
 * The desktop puts the two side by side because the whole point is glancing
 * between them. A phone cannot show both, so they are two tabs over one search
 * box, and the balance of every customer is a card carrying its own total —
 * the number the desktop grid put in a column is here the biggest thing on the
 * card, because "how much" is the only question a phone is asked.
 */

const money = (v) => `₹${(v || 0).toLocaleString('en-IN')}`;

const STATUS_TONE = { fully_paid: 'positive', partially_paid: 'caution', unpaid: 'critical' };

function Tile({ label, value, tone, testID }) {
  const color = { critical: Colors.semantic.critical, positive: Colors.semantic.positive, accent: Colors.accent.DEFAULT }[tone];
  return (
    <div
      data-testid={testID}
      className="min-w-0 rounded-[14px] border p-3.5"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <p className="truncate text-[11px] font-semibold uppercase tracking-[0.6px]" style={{ color: Colors.text.secondary }}>{label}</p>
      <p className="mt-1 truncate text-[20px] font-bold tabular-nums -tracking-[0.3px]" style={{ color: color || Colors.text.primary }}>
        {value}
      </p>
    </div>
  );
}

function CustomerCard({ customer: c, onOpen }) {
  return (
    <li className="mt-2">
      <button
        type="button"
        data-testid={`owing-card-${c._id}`}
        onClick={onOpen}
        className="block w-full rounded-[10px] border px-4 py-3 text-left"
        style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
      >
        <span className="flex items-start justify-between gap-3">
          <span className="min-w-0">
            <span className="block truncate text-[15px] font-bold" style={{ color: Colors.text.primary }}>{c.customer_name}</span>
            {!!c.firm_name && (
              <span className="block truncate text-[12px]" style={{ color: Colors.text.secondary }}>{c.firm_name}</span>
            )}
          </span>
          <span className="shrink-0 text-right text-[17px] font-bold tabular-nums" style={{ color: Colors.semantic.critical }}>
            {money(c.outstanding)}
          </span>
        </span>
        <span className="mt-2 block text-[12px] tabular-nums" style={{ color: Colors.text.tertiary }}>
          paid <span style={{ color: Colors.semantic.positive }}>{money(c.total_paid)}</span> of {money(c.total_value)}
          {' · '}{c.order_count} order{c.order_count === 1 ? '' : 's'}
        </span>
      </button>
    </li>
  );
}

function PaymentCard({ payment: p, onOpen }) {
  return (
    <li className="mt-2">
      <button
        type="button"
        onClick={onOpen}
        className="block w-full rounded-[10px] border px-4 py-3 text-left"
        style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
      >
        <span className="flex items-start justify-between gap-3">
          <span className="min-w-0">
            <span className="block truncate text-[15px] font-bold" style={{ color: Colors.text.primary }}>{p.customer_name}</span>
            <span className="block truncate text-[12px]" style={{ color: Colors.text.secondary }}>
              {p.order_id}
              {p.payment?.recorded_by_name ? ` · ${p.payment.recorded_by_name}` : ''}
            </span>
          </span>
          <span className="flex shrink-0 flex-col items-end gap-1">
            <span className="text-[17px] font-bold tabular-nums" style={{ color: Colors.semantic.positive }}>
              {money(p.payment?.amount)}
            </span>
            {!!p.payment?.mode && <StatusPill label={p.payment.mode} tone="neutral" />}
          </span>
        </span>
        <span className="mt-2 block text-[12px]" style={{ color: Colors.text.tertiary }}>
          {p.payment?.date ? formatIST(p.payment.date, { compact: true }) : '—'}
        </span>
      </button>
    </li>
  );
}

export default function PhonePayments({
  loading, overview, owingCount, outstanding, recent, totals,
  view, onView, onSearchOwing, onSearchRecent, onRefresh, onOpenOrder, onOpenCustomer,
}) {
  const breakdown = Object.entries(overview?.status_breakdown || {});
  const tabs = [
    { key: 'owing', label: `Owing ${owingCount}`, testID: 'payments-tab-owing' },
    { key: 'recent', label: `Recent ${recent.length}`, testID: 'payments-tab-recent' },
  ];

  return (
    <PhonePage testID="payments-phone">
      <PageHeader
        title="Payments"
        subtitle={`${owingCount} customer${owingCount === 1 ? '' : 's'} owing`}
        action={<IconButton icon={RefreshCw} label="Refresh" onClick={onRefresh} testID="refresh-btn" />}
      />

      {loading ? <ScreenLoader /> : (
        <div className="flex flex-col gap-3 px-5 pb-8">
          <div className="grid grid-cols-2 gap-3" data-testid="payment-stats">
            <Tile label="Outstanding" value={money(overview?.total_outstanding)} tone="critical" testID="tile-outstanding" />
            <Tile label="Billed" value={money(overview?.total_billed)} tone="accent" />
            <Tile label="This month" value={money(overview?.collections_this_month)} tone="positive" />
            <Tile label="This week" value={money(overview?.collections_this_week)} />
          </div>
          {breakdown.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {breakdown.map(([status, n]) => (
                <StatusPill key={status} label={`${n} ${status.replace('_', ' ')}`} tone={STATUS_TONE[status]} dot />
              ))}
            </div>
          )}

          <TabRow tabs={tabs} active={view} onSelect={onView} />

          {view === 'owing' ? (
            <>
              <SearchBar key="owing" placeholder="Customer or firm…" onChange={onSearchOwing} testID="owing-search-input" />
              {outstanding.length === 0 ? (
                <EmptyState icon={IndianRupee} message="No outstanding payments" />
              ) : (
                <>
                  <p className="text-[12px] tabular-nums" style={{ color: Colors.text.secondary }} data-testid="owing-total">
                    {money(totals.outstanding)} outstanding across {outstanding.length} customer{outstanding.length === 1 ? '' : 's'}
                    {' · '}{money(totals.paid)} paid of {money(totals.value)}
                  </p>
                  <ul data-testid="owing-list" className="-mt-1">
                    {outstanding.map((c) => <CustomerCard key={c._id} customer={c} onOpen={() => onOpenCustomer(c)} />)}
                  </ul>
                </>
              )}
            </>
          ) : (
            <>
              <SearchBar key="recent" placeholder="Order, customer, firm…" onChange={onSearchRecent} testID="recent-search-input" />
              {recent.length === 0 ? (
                <EmptyState icon={IndianRupee} message="No recent payments" />
              ) : (
                <ul data-testid="recent-list" className="-mt-1">
                  {recent.map((p, i) => <PaymentCard key={i} payment={p} onOpen={() => onOpenOrder(p)} />)}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </PhonePage>
  );
}
